import Navigation from '../components/Navigation'

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-8">Privacy Policy</h1>

        <div className="prose prose-lg dark:prose-invert max-w-none">
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            <strong>Last Updated:</strong> October 22, 2025
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">1. Introduction</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              ContentIndexer ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy
              explains how we collect, use, disclose, and safeguard your information when you use our content
              analysis and indexing service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">2. Information We Collect</h2>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">2.1 Account Information</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              When you register through OAuth providers (GitHub, Google), we collect:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Email address</li>
              <li>Name (if provided by OAuth provider)</li>
              <li>Profile picture (if provided by OAuth provider)</li>
              <li>OAuth provider identifier</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">2.2 Content You Upload</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              To provide our service, we process:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Video, audio, and text files you upload for analysis</li>
              <li>URLs of content you submit for processing</li>
              <li>Analysis results and extracted metadata</li>
              <li>Job configurations and workflow selections</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">2.3 Usage Data</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We automatically collect:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>API requests and usage patterns</li>
              <li>Processing time and resource consumption</li>
              <li>Credit usage and transaction history</li>
              <li>Error logs and performance metrics</li>
              <li>IP addresses and browser information</li>
              <li>Device type and operating system</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">2.4 Payment Information</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Payment processing is handled by Stripe. We do not store full credit card information. We retain:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Transaction IDs and payment records</li>
              <li>Billing history and credit purchases</li>
              <li>Last 4 digits of payment cards (for reference)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">2.5 Analytics Information</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We use Google Analytics to understand how our service is used. Google Analytics collects:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Page views and navigation patterns</li>
              <li>User interactions and button clicks</li>
              <li>Device information and browser type</li>
              <li>Anonymized user identifiers</li>
              <li>Conversion events (sign-ups, purchases)</li>
              <li>Geographic location (country/city level)</li>
            </ul>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              You can opt out of Google Analytics by installing the{' '}
              <a
                href="https://tools.google.com/dlpage/gaoptout"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 dark:text-primary-400 hover:underline"
              >
                Google Analytics Opt-out Browser Add-on
              </a>
              .
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We use collected information to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Provide and maintain our content analysis service</li>
              <li>Process your content and generate analysis results</li>
              <li>Manage your account and authenticate users</li>
              <li>Process payments and manage credits</li>
              <li>Send service-related notifications and updates</li>
              <li>Improve our service and develop new features</li>
              <li>Monitor usage for security and fraud prevention</li>
              <li>Comply with legal obligations</li>
              <li>Respond to support requests and customer inquiries</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">4. Data Storage and Security</h2>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">4.1 Storage Infrastructure</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Your data is stored on Cloudflare's global infrastructure:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Content files: Cloudflare R2 object storage</li>
              <li>User data and metadata: Cloudflare D1 database</li>
              <li>Processing queues: Cloudflare Queues</li>
              <li>All data is encrypted at rest and in transit</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">4.2 Security Measures</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We implement industry-standard security practices:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>TLS/SSL encryption for all data transmission</li>
              <li>Secure OAuth 2.0 authentication</li>
              <li>API key-based access control</li>
              <li>Regular security audits and monitoring</li>
              <li>Access controls and authentication requirements</li>
              <li>Automated backup and disaster recovery</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">4.3 Data Retention</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We retain your data as follows:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Account information: Until account deletion</li>
              <li>Uploaded content: Until you delete it or 90 days of inactivity</li>
              <li>Analysis results: Until you delete them or 1 year</li>
              <li>Transaction history: 7 years for legal compliance</li>
              <li>Usage logs: 90 days</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">5. Data Sharing and Disclosure</h2>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">5.1 Third-Party Services</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We share data with the following service providers:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li><strong>Cloudflare:</strong> Infrastructure and CDN services</li>
              <li><strong>Stripe:</strong> Payment processing</li>
              <li><strong>OAuth Providers:</strong> GitHub and Google for authentication</li>
              <li><strong>Google Analytics:</strong> Website analytics and user behavior tracking (see{' '}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Google's Privacy Policy
                </a>
                )
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">5.2 Legal Requirements</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We may disclose your information when required by law:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>To comply with legal obligations or court orders</li>
              <li>To protect our rights, property, or safety</li>
              <li>To prevent fraud or security threats</li>
              <li>With your consent or at your direction</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">5.3 Business Transfers</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              If we are involved in a merger, acquisition, or sale of assets, your information may be transferred
              as part of that transaction. We will notify you before your data is transferred and subject to a
              different privacy policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">6. Your Rights and Choices</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              You have the following rights regarding your personal information:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and data</li>
              <li><strong>Export:</strong> Download your content and analysis results</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
              <li><strong>Object:</strong> Object to certain types of data processing</li>
            </ul>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              To exercise these rights, contact us through our support channels. We will respond within 30 days.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">7. Cookies and Tracking</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We use essential cookies and similar technologies:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li><strong>Essential cookies:</strong> Required for authentication and service functionality</li>
              <li><strong>Local storage:</strong> Store user preferences and session data</li>
              <li><strong>Analytics:</strong> Track usage patterns to improve our service</li>
            </ul>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              You can control cookies through your browser settings, but disabling essential cookies may affect
              service functionality.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">8. Children's Privacy</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Our service is not intended for children under 13 years of age. We do not knowingly collect personal
              information from children. If we discover we have collected information from a child, we will delete
              it immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">9. International Data Transfers</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Our service uses Cloudflare's global network, which means your data may be processed in various
              countries. We ensure appropriate safeguards are in place for international data transfers in
              compliance with applicable laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">10. GDPR Compliance (European Users)</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              If you are in the European Economic Area (EEA), you have additional rights under GDPR:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Right to data portability</li>
              <li>Right to restrict processing</li>
              <li>Right to object to automated decision-making</li>
              <li>Right to lodge a complaint with a supervisory authority</li>
            </ul>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Our legal basis for processing your data is: (a) your consent, (b) performance of a contract,
              (c) legal obligations, or (d) legitimate interests.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">11. CCPA Compliance (California Users)</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              If you are a California resident, you have additional rights under CCPA:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Right to know what personal information we collect and how we use it</li>
              <li>Right to delete personal information</li>
              <li>Right to opt-out of the sale of personal information (we do not sell data)</li>
              <li>Right to non-discrimination for exercising privacy rights</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">12. Changes to Privacy Policy</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We may update this Privacy Policy from time to time. We will notify you of significant changes via
              email or through a prominent notice on our service. The "Last Updated" date at the top reflects
              when changes were last made.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">13. Contact Us</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              If you have questions about this Privacy Policy or want to exercise your privacy rights,
              please contact us through our support channels.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">14. Data Breach Notification</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              In the event of a data breach that affects your personal information, we will notify you and
              relevant authorities as required by applicable law, typically within 72 hours of becoming aware
              of the breach.
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center gap-6 mb-4">
              <a href="/terms" className="text-gray-400 hover:text-white transition-colors">
                Terms of Use
              </a>
              <a href="/privacy" className="text-gray-400 hover:text-white transition-colors">
                Privacy Policy
              </a>
            </div>
            <p className="text-gray-400">&copy; 2025 ContentIndexer. Built with Cloudflare Workers & React.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
